// Calendar arithmetic on `YYYY-MM-DD` / `YYYY-MM` strings. Everything runs on Date.UTC so the
// browser's own zone never leaks in; "which day is it in the visitor's zone" comes from Intl.

/** `YYYY-MM-DD` */
export type ISODate = string;
/** `YYYY-MM` */
export type ISOMonth = string;
/** ISO weekday, Monday = 1 … Sunday = 7 */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

const pad = (n: number): string => (n < 10 ? `0${n}` : String(n));

export function parseISODate(date: ISODate): [year: number, month: number, day: number] {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  return [y, m, d];
}

export function toISODate(year: number, month: number, day: number): ISODate {
  return `${year}-${pad(month)}-${pad(day)}`;
}

export function isISODate(value: unknown): value is ISODate {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export function isISOMonth(value: unknown): value is ISOMonth {
  return typeof value === 'string' && /^\d{4}-\d{2}$/.test(value);
}

function fromUtc(ms: number): ISODate {
  const d = new Date(ms);
  return toISODate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

export function addDays(date: ISODate, days: number): ISODate {
  const [y, m, d] = parseISODate(date);
  return fromUtc(Date.UTC(y, m - 1, d + days));
}

export function addMonths(month: ISOMonth, months: number): ISOMonth {
  const [y, m] = parseISODate(`${month}-01`);
  const d = new Date(Date.UTC(y, m - 1 + months, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

export function monthOf(date: ISODate): ISOMonth {
  return date.slice(0, 7);
}

export function daysInMonth(month: ISOMonth): number {
  const [y, m] = parseISODate(`${month}-01`);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function monthRange(month: ISOMonth): { from: ISODate; to: ISODate } {
  return { from: `${month}-01`, to: `${month}-${pad(daysInMonth(month))}` };
}

export function monthsBetween(from: ISODate, to: ISODate): ISOMonth[] {
  const months: ISOMonth[] = [];
  let m = monthOf(from);
  const last = monthOf(to);
  while (m <= last && months.length < 4) {
    months.push(m);
    m = addMonths(m, 1);
  }
  return months;
}

export function weekdayOf(date: ISODate): Weekday {
  const [y, m, d] = parseISODate(date);
  return (((new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7) + 1) as Weekday;
}

const dateFormatters = new Map<string, Intl.DateTimeFormat>();

/** The calendar date (`YYYY-MM-DD`) that `instant` falls on in `timeZone`. */
export function dateIn(timeZone: string, instant: number | string | Date = Date.now()): ISODate {
  let formatter = dateFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    dateFormatters.set(timeZone, formatter);
  }
  const parts = formatter.formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return toISODate(get('year'), get('month'), get('day'));
}

export const todayIn = (timeZone: string): ISODate => dateIn(timeZone);

// Regions whose calendars start on Sunday; used only when `Intl.Locale` has no week info.
const SUNDAY_FIRST = new Set([
  'US',
  'CA',
  'JP',
  'BR',
  'MX',
  'IL',
  'IN',
  'PH',
  'ZA',
  'KR',
  'TW',
  'HK',
  'AU',
  'NZ',
  'SA',
  'AE',
]);

export function firstDayOfWeek(locale: string): Weekday {
  try {
    const tag = new Intl.Locale(locale);
    type WeekInfo = { firstDay?: number };
    type LocaleWithWeek = Intl.Locale & { getWeekInfo?: () => WeekInfo; weekInfo?: WeekInfo };
    const info = (tag as LocaleWithWeek).getWeekInfo?.() ?? (tag as LocaleWithWeek).weekInfo;
    const first = info?.firstDay;
    if (first && first >= 1 && first <= 7) return first as Weekday;
    const region = tag.maximize().region ?? '';
    return SUNDAY_FIRST.has(region) ? 7 : 1;
  } catch {
    return 1;
  }
}

export interface MonthCell {
  date: ISODate;
  inMonth: boolean;
}

/** Six rows of seven cells so the grid never changes height between months. */
export function monthGrid(month: ISOMonth, firstDay: Weekday): MonthCell[][] {
  const first: ISODate = `${month}-01`;
  const offset = (weekdayOf(first) - firstDay + 7) % 7;
  const start = addDays(first, -offset);
  const rows: MonthCell[][] = [];
  for (let r = 0; r < 6; r++) {
    const row: MonthCell[] = [];
    for (let c = 0; c < 7; c++) {
      const date = addDays(start, r * 7 + c);
      row.push({ date, inMonth: monthOf(date) === month });
    }
    rows.push(row);
  }
  return rows;
}

export function weekdayLabels(
  locale: string,
  firstDay: Weekday
): { short: string; long: string }[] {
  // 2024-01-01 is a Monday, so index i is ISO weekday i + 1.
  const shortFmt = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'short' });
  const longFmt = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'long' });
  const out: { short: string; long: string }[] = [];
  for (let i = 0; i < 7; i++) {
    const weekday = ((firstDay - 1 + i) % 7) + 1;
    const d = new Date(Date.UTC(2024, 0, weekday));
    out.push({ short: shortFmt.format(d), long: longFmt.format(d) });
  }
  return out;
}
